/**
 * ============================================================================
 * RiskRankingDashboard — Phase 6d frontend (Supplier Management Module).
 * Owner: Claude 3 (Frontend & UX).
 * Route: /suppliers/risk-ranking
 * Purpose: bucketed Kanban view of every supplier, partitioned by risk
 *          category (CRITICAL / MAJOR / MINOR / LOW). Each card opens the
 *          assessment-history drawer.
 *
 * UX RULES (drafts/42_ux_polish_and_dual_brand_design.md):
 *   - Columns are visually distinct via header tint, not just label.
 *   - Per-site filter is a single dropdown when the tenant is multi-site.
 * ============================================================================ */

import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Card, CardBody, CardHeader, PageHero } from '@edms/design-system';
import wstyles from '../WebhookAdmin/WebhookAdmin.module.css';
import styles from './RiskRankingDashboard.module.css';
import { RiskBucketColumn } from './RiskBucketColumn';
import { RiskAssessmentDrawer } from './RiskAssessmentDrawer';
import {
  useRiskRankingQuery,
  type RiskRankingBucket,
  type RiskRankingCard,
} from './useRiskRankingQuery';
import type { RiskCategory } from '../SupplierWorkbench/useSuppliersQuery';

const BUCKET_ORDER: RiskCategory[] = ['CRITICAL', 'MAJOR', 'MINOR', 'LOW'];

export function RiskRankingDashboard() {
  const { t, i18n } = useTranslation();
  const [site, setSite] = useState<string>('');
  const [drawerCard, setDrawerCard] = useState<RiskRankingCard | null>(null);

  const rankingQ = useRiskRankingQuery(site || undefined);
  const dateFmt = useMemo(
    () =>
      new Intl.DateTimeFormat(i18n.language, {
        dateStyle: 'medium',
        timeStyle: 'short',
      }),
    [i18n.language],
  );

  const data = rankingQ.data;
  const generatedAtUtc = data?.generated_at_utc ?? new Date().toISOString();

  // Normalize: server may return buckets in any order; we present them in
  // CRITICAL → LOW order. Missing buckets render as empty columns.
  const orderedBuckets: RiskRankingBucket[] = useMemo(() => {
    const map = new Map<RiskCategory, RiskRankingBucket>();
    (data?.buckets ?? []).forEach((b) => map.set(b.category, b));
    return BUCKET_ORDER.map(
      (c) => map.get(c) ?? { category: c, count: 0, cards: [] },
    );
  }, [data?.buckets]);

  return (
    <div className={wstyles.adminScreen}>
      <PageHero
        eyebrow={t('supplier.riskRanking.eyebrow', { defaultValue: 'Suppliers' })}
        title={t('supplier.riskRanking.title', { defaultValue: 'Supplier risk ranking' })}
        subtitle={t('supplier.riskRanking.subtitle', {
          defaultValue:
            'Bucketed view of every supplier by current risk category. Click a card to see its assessment history.',
        })}
      />
      {data?.sites && data.sites.length > 0 && (
        <div className={styles.siteFilter}>
          <label htmlFor="rrd-site" className={styles.siteLabel}>
            {t('supplier.riskRanking.siteFilter', { defaultValue: 'Site' })}
          </label>
          <select
            id="rrd-site"
            className={styles.siteSelect}
            value={site}
            onChange={(e) => setSite(e.target.value)}
          >
            <option value="">
              {t('supplier.riskRanking.allSites', { defaultValue: 'All sites' })}
            </option>
            {data.sites.map((s) => (
              <option key={s.code} value={s.code}>
                {s.label}
              </option>
            ))}
          </select>
        </div>
      )}

      <Card>
        <CardHeader>
          <div className={styles.headerRow}>
            <h2 className={wstyles.sectionTitle}>
              {t('supplier.riskRanking.boardTitle', {
                defaultValue: 'Risk board',
              })}
            </h2>
            <span className={styles.generatedAt} aria-live="polite">
              {t('supplier.riskRanking.generatedAt', {
                defaultValue: 'Generated {{ts}}',
                ts: dateFmt.format(new Date(generatedAtUtc)),
              })}
            </span>
          </div>
        </CardHeader>
        <CardBody>
          {rankingQ.isLoading && (
            <div className={styles.board} aria-hidden="true">
              {BUCKET_ORDER.map((c) => (
                <div key={c} className={styles.colSkeleton} />
              ))}
            </div>
          )}

          {rankingQ.isError && (
            <div className={styles.errorBanner} role="alert">
              <span>
                {t('supplier.riskRanking.errors.loadFailed', {
                  defaultValue: 'Could not load the risk ranking.',
                })}
              </span>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => rankingQ.refetch()}
              >
                {t('common.retry', { defaultValue: 'Retry' })}
              </Button>
            </div>
          )}

          {!rankingQ.isLoading && !rankingQ.isError && (
            <div
              className={styles.board}
              role="list"
              aria-label={t('supplier.riskRanking.boardAria', {
                defaultValue: 'Risk-bucketed Kanban board',
              })}
            >
              {orderedBuckets.map((b) => (
                <div key={b.category} role="listitem">
                  <RiskBucketColumn bucket={b} onOpenCard={setDrawerCard} />
                </div>
              ))}
            </div>
          )}
        </CardBody>
      </Card>

      {drawerCard && (
        <RiskAssessmentDrawer
          card={drawerCard}
          onClose={() => setDrawerCard(null)}
        />
      )}
    </div>
  );
}
